import { api } from "@/lib/apiClient";
import { isClaimedHeroCustomFieldLabel } from "@/lib/projectReferenzFields";

/* ─────────────────────────── Typen ─────────────────────────── */

export interface HeroCustomFieldValue {
  schema_id?: string;
  schemaId?: string;
  property_uuid?: string;
  propertyUuid?: string;
  uuid?: string;
  label?: string | null;
  value?: string | number | boolean | null;
  type?: string | null;
}

export interface AuftragTimesheetNumber {
  number: string;
  timesheet_number: number;
  display_number?: string | null;
  status?: string | null;
}

export interface AuftragTimesheetSummary {
  id?: number;
  status?: string | null;
  status_display?: string | null;
  hours_total?: number | null;
  display_number?: string | null;
  number?: string | null;
  timesheet_number?: number | string | null;
}

export interface TagOut {
  id: number;
  name: string;
  color?: string | null;
}

export interface KalkStundenAppEntry {
  lohnart_id: string;
  bezeichnung: string;
  stunden: number;
  sichtbarkeit?: "alle" | "buero" | string;
}

export interface Order {
  id: number;
  hero_project_id?: number | null;
  auftragsnummer: string;
  ansprechpartner_id?: number | null;
  ansprechpartner?: string | null;
  kunde: string;
  hero_kunde?: string | null;
  use_hero_customer_name_in_projects?: boolean;
  kunden_id?: number | null;
  /** Kundencode des Ordners – steuert, welche Kunden im Bearbeiten-Dialog wählbar sind. */
  kunden_code?: number | null;
  ort: string;
  ort_zusatz?: string | null;
  beschreibung?: string | null;
  ordnernummer: string;
  ordner_id?: number | null;
  sordner_id?: number | null;
  ordnerpfad?: string | null;
  sharepoint_path?: string | null;
  sharepoint_folder_id?: string | null;
  has_folder?: boolean | null;
  legacy_import?: boolean | null;
  historische_rechnungsnummer?: string | null;
  historische_lieferscheinnummer?: string | null;
  historische_info?: string | null;
  ort_id?: number | null;
  jahr?: number | null;
  /** Jahr des Ordner-Datensatzes. Kann vom Projektjahr abweichen. */
  ordner_jahr?: number | null;
  hero_custom_summary?: string | null;
  hero_custom_fields?: HeroCustomFieldValue[] | null;
  tags?: TagOut[];
  project_completed?: boolean | null;
  project_completion_text?: string | null;
  project_completion_created_at?: string | null;
  project_completion_created_by?: string | null;
  project_completion_pdf_share_url?: string | null;
  muendlich_beauftragt_at?: string | null;
  muendlich_beauftragt_von?: string | null;
  muendlich_beauftragt_notiz?: string | null;
  muendlich_beauftragt_statement?: string | null;
  muendlich_beauftragt_pdf_item_id?: string | null;
  muendlich_beauftragt_pdf_share_url?: string | null;
  hero_address_id?: number | null;
  hero_customer_id?: number | null;
  /** App-Kunde (geschaeftspartner.id), auch ohne Hero-Verknüpfung. */
  geschaeftspartner_id?: number | null;
  /** Firmenname aus den App-Kontakten, wenn am Projekt ein Geschäftspartner hängt. */
  gp_firmenname?: string | null;
  gp_hero_contact_id?: number | null;
  gp_has_logo?: boolean | null;
  gp_logo_updated_at?: string | null;
  hero_contact_id?: number | null;
  hero_step_id?: number | null;
  hero_step_name?: string | null;
  timesheet_numbers?: AuftragTimesheetNumber[] | null;
  timesheets?: AuftragTimesheetSummary[] | null;
  created_at?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  commissioned_gd_document_id?: number | null;
  angebot_commission_status?: "beauftragt" | "abgelehnt" | null;
  /** True, wenn in der App ein Angebot (GD) für dieses Projekt existiert. */
  has_app_angebot?: boolean | null;
  /** Aus App-Angebot abgeleitet: "Festpreis" | "Nachweis" | "NW + FP" */
  auftragsart_app?: string | null;
  /** Festpreis-Stunden aus App-Angebot, nach Lohnart (sichtbarkeitsgefiltert). */
  kalk_stunden_app?: KalkStundenAppEntry[] | null;
  /** Angebotene Nachweis-Stunden aus App-Angebot (Summe). */
  nachweis_stunden_app?: number | null;
  zahlungsart?: "rechnung" | "vorkasse" | null;
  zahlungsziel_tage?: number | null;
  skonto_prozent?: number | null;
  skontoziel_tage?: number | null;
  workflow_status?: ProjectWorkflowStatusKey | string | null;
  workflow_status_label?: string | null;
  workflow_status_secondary?: ProjectWorkflowStatusKey | string | null;
  workflow_status_secondary_label?: string | null;
  workflow_status_manual?: boolean | null;
  late_hour_permission?: boolean | null;
  /** Execution Class für Stücklisten (projektweit). */
  execution_class?: "EXC1" | "EXC2" | string | null;
  /** Kunden-Bestellnummer (native, ehemals Hero-Eigenfeld). */
  bestellnummer?: string | null;
  /** regel = aus Kundenregel, manuell = überschrieben. */
  bestellnummer_quelle?: "regel" | "manuell" | null;
  /** Zuletzt aufgelöster Wert aus den Kundenregeln. */
  bestellnummer_regel_wert?: string | null;
  /** Kundenreferenz (native, ehemals Hero-Eigenfeld). */
  kundenreferenz?: string | null;
  /** Leitweg-ID des Auftrags (E-Rechnung BT-10). */
  leitweg_id?: string | null;
  /** Hybride Suche: Gesamtscore (nur bei aktiver Suche `q` gesetzt). */
  search_score?: number | null;
  /** Hybride Suche: Trefferklasse (nur mit `debug=true` gefüllt). */
  match_class?: number | null;
  /** Hybride Suche: Treffergründe (vereinfachte Labels, bei `debug` Codes). */
  match_reasons?: string[] | null;
  /** Zusammengeführt nach dieser führenden Akte. */
  merged_into_auftrag_id?: number | null;
  merged_into_auftragsnummer?: string | null;
  korrektur?: {
    id: number;
    falltyp?: string | null;
    created_at?: string | null;
    leading_auftrag_id?: number;
    leading_auftragsnummer?: string | null;
    reference_auftrag_id?: number;
    reference_auftragsnummer?: string | null;
  } | null;
  korrektur_quellen?: {
    auftrag_id: number;
    auftragsnummer?: string | null;
    falltyp?: string | null;
    created_at?: string | null;
  }[];
}

export type ProjectExecutionClass = "EXC1" | "EXC2";

export function patchProjectExecutionClass(
  orderId: number,
  executionClass: ProjectExecutionClass | null,
): Promise<Order> {
  return api.patch<Order>(
    `/auftraege/${encodeURIComponent(String(orderId))}/execution-class`,
    { execution_class: executionClass },
  );
}

export function projectExecutionClassBadgeClass(
  executionClass: string | null | undefined,
): string {
  const key = String(executionClass || "").trim().toUpperCase();
  if (key === "EXC2") return "eh-exc-badge eh-exc-badge--exc2";
  if (key === "EXC1") return "eh-exc-badge eh-exc-badge--exc1";
  return "eh-exc-badge";
}

export const PROJECT_WORKFLOW_STATUSES = [
  "offen",
  "anfrage",
  "angeboten",
  "abgelehnt",
  "beauftragt",
  "terminiert",
  "in_bearbeitung",
  "bereit_zur_montage",
  "fertiggestellt",
  "vorkasse",
  "teilabgerechnet",
  "abgerechnet",
  "teilbezahlt",
  "teilrechnung_bezahlt",
  "bezahlt",
] as const;

export type ProjectWorkflowStatusKey = (typeof PROJECT_WORKFLOW_STATUSES)[number];

export const PROJECT_WORKFLOW_STATUS_LABELS: Record<ProjectWorkflowStatusKey, string> = {
  offen: "Offen",
  anfrage: "Anfrage",
  angeboten: "Angeboten",
  abgelehnt: "Abgelehnt",
  beauftragt: "Beauftragt",
  terminiert: "Terminiert",
  in_bearbeitung: "In Bearbeitung",
  bereit_zur_montage: "Bereit zur Montage",
  fertiggestellt: "Fertiggestellt",
  vorkasse: "Vorkasse",
  teilabgerechnet: "Teilabgerechnet",
  abgerechnet: "Abgerechnet",
  teilbezahlt: "Teilbezahlt",
  teilrechnung_bezahlt: "Teilrechnung bezahlt",
  bezahlt: "Bezahlt",
};

export type ProjectWorkflowTone =
  | "neutral"
  | "blue"
  | "indigo"
  | "red"
  | "sky"
  | "teal"
  | "orange"
  | "purple"
  | "green"
  | "yellow"
  | "pink"
  | "sand"
  | "lime"
  | "violet"
  | "forest";

export function normalizeProjectWorkflowStatus(
  value: string | null | undefined,
): ProjectWorkflowStatusKey {
  const key = String(value || "offen").trim().toLowerCase();
  return (PROJECT_WORKFLOW_STATUSES as readonly string[]).includes(key)
    ? (key as ProjectWorkflowStatusKey)
    : "offen";
}

export type ProjectWorkflowStatusView = {
  key: ProjectWorkflowStatusKey;
  label: string;
  tone: ProjectWorkflowTone;
  secondary?: { key: ProjectWorkflowStatusKey; label: string; tone: ProjectWorkflowTone };
};

export function projectWorkflowStatus(
  project: Pick<
    Order,
    | "workflow_status"
    | "workflow_status_label"
    | "workflow_status_secondary"
    | "workflow_status_secondary_label"
    | "project_completed"
    | "angebot_commission_status"
  >,
): ProjectWorkflowStatusView {
  let primary: ProjectWorkflowStatusView;
  if (project.workflow_status) {
    const key = normalizeProjectWorkflowStatus(project.workflow_status);
    const label =
      project.workflow_status_label?.trim() || PROJECT_WORKFLOW_STATUS_LABELS[key];
    primary = { key, label, tone: projectWorkflowStatusTone(key) };
  } else if (project.angebot_commission_status === "beauftragt") {
    // Kompatibilität, falls API noch ohne workflow_status antwortet
    primary = { key: "beauftragt", label: "Beauftragt", tone: "sky" };
  } else if (project.angebot_commission_status === "abgelehnt") {
    primary = { key: "abgelehnt", label: "Abgelehnt", tone: "red" };
  } else if (project.project_completed) {
    primary = { key: "fertiggestellt", label: "Fertiggestellt", tone: "green" };
  } else {
    primary = { key: "offen", label: "Offen", tone: "neutral" };
  }

  if (project.workflow_status_secondary) {
    const secondaryKey = normalizeProjectWorkflowStatus(project.workflow_status_secondary);
    if (secondaryKey !== primary.key) {
      primary.secondary = {
        key: secondaryKey,
        label:
          project.workflow_status_secondary_label?.trim() ||
          PROJECT_WORKFLOW_STATUS_LABELS[secondaryKey],
        tone: projectWorkflowStatusTone(secondaryKey),
      };
    }
  }
  return primary;
}

export function projectWorkflowStatusTone(key: ProjectWorkflowStatusKey): ProjectWorkflowTone {
  switch (key) {
    case "anfrage":
      return "blue";
    case "angeboten":
      return "indigo";
    case "abgelehnt":
      return "red";
    case "beauftragt":
      return "sky";
    case "terminiert":
      return "teal";
    case "in_bearbeitung":
      return "orange";
    case "bereit_zur_montage":
      return "purple";
    case "fertiggestellt":
      return "green";
    case "vorkasse":
      return "yellow";
    case "teilabgerechnet":
      return "pink";
    case "abgerechnet":
      return "sand";
    case "teilbezahlt":
      return "lime";
    case "teilrechnung_bezahlt":
      return "violet";
    case "bezahlt":
      return "forest";
    case "offen":
    default:
      return "neutral";
  }
}

/** CSS-Klassen wie Nachweis-Nummern (`eh-ts-badge`). */
export function projectWorkflowStatusBadgeClass(key: ProjectWorkflowStatusKey | string): string {
  const normalized = normalizeProjectWorkflowStatus(key);
  return `eh-wf-badge eh-wf-badge--${normalized}`;
}

/** @deprecated Prefer projectWorkflowStatusBadgeClass(key) for pill badges. */
export function projectWorkflowStatusClass(tone: ProjectWorkflowTone): string {
  return projectWorkflowStatusBadgeClass(
    (
      {
        neutral: "offen",
        blue: "anfrage",
        indigo: "angeboten",
        red: "abgelehnt",
        sky: "beauftragt",
        teal: "terminiert",
        orange: "in_bearbeitung",
        purple: "bereit_zur_montage",
        green: "fertiggestellt",
        yellow: "vorkasse",
        pink: "teilabgerechnet",
        sand: "abgerechnet",
        lime: "teilbezahlt",
        violet: "teilrechnung_bezahlt",
        forest: "bezahlt",
      } as const
    )[tone],
  );
}

export async function patchProjectWorkflowStatus(
  orderId: number,
  payload: {
    workflow_status?: ProjectWorkflowStatusKey | string | null;
    enabled: boolean;
    begruendung: string;
    /** Löst die Beauftragung, damit Angeboten oder Offen stehen bleiben. */
    beauftragung_zuruecknehmen?: boolean;
    notiz?: string | null;
  },
): Promise<Order> {
  return api.patch<Order>(
    `/auftraege/${encodeURIComponent(String(orderId))}/workflow-status`,
    {
      workflow_status: payload.workflow_status ?? undefined,
      enabled: payload.enabled,
      begruendung: payload.begruendung,
      beauftragung_zuruecknehmen: payload.beauftragung_zuruecknehmen ?? false,
      notiz: payload.notiz ?? undefined,
    },
  );
}

/** Beauftragt über Angebot, mündliche Beauftragung oder den Status selbst. */
export function projectHasCommissionToRevoke(
  project: Pick<
    Order,
    | "angebot_commission_status"
    | "commissioned_gd_document_id"
    | "muendlich_beauftragt_at"
    | "workflow_status"
  >,
): boolean {
  if (project.angebot_commission_status === "beauftragt") return true;
  if (project.commissioned_gd_document_id) return true;
  if (project.muendlich_beauftragt_at) return true;
  return normalizeProjectWorkflowStatus(project.workflow_status) === "beauftragt";
}

export function statusChangeRevokesCommission(
  project: Parameters<typeof projectHasCommissionToRevoke>[0],
  status: ProjectWorkflowStatusKey | string,
): boolean {
  if (status !== "angeboten" && status !== "offen") return false;
  return projectHasCommissionToRevoke(project);
}

export type CommissionRevokePreview = {
  ziel_status: ProjectWorkflowStatusKey | string;
  ziel_label: string;
  aktueller_status: ProjectWorkflowStatusKey | string;
  aktueller_label: string;
  can_apply: boolean;
  actions: string[];
  blockers: string[];
  hero_unveraendert: boolean;
};

export async function getCommissionRevokePreview(
  orderId: number,
  zielStatus: "angeboten" | "offen",
): Promise<CommissionRevokePreview> {
  const params = new URLSearchParams({ ziel_status: zielStatus });
  return api.get<CommissionRevokePreview>(
    `/auftraege/${encodeURIComponent(String(orderId))}/beauftragung-zuruecknehmen/preview?${params}`,
  );
}

export type ProjectRejectHoursAction = "umbuchen" | "verfallen";

export type ProjectRejectPayload = {
  begruendung: string;
  stunden_aktion?: ProjectRejectHoursAction | null;
  ziel_auftrag_id?: number | null;
};

export type ProjectRejectResult = {
  auftrag_id: number;
  workflow_status: string;
  note_id: number | null;
  stunden_aktion?: ProjectRejectHoursAction | null;
  ziel_auftrag_id?: number | null;
};

export type ProjectRejectPreview = {
  has_hours: boolean;
  hours_decimal: number;
  employee_time_count: number;
  timesheet_count: number;
  subcontractor_time_count: number;
  timer_count: number;
};

export async function getProjectRejectPreview(orderId: number): Promise<ProjectRejectPreview> {
  return api.get<ProjectRejectPreview>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ablehnen/preview`,
  );
}

export async function rejectProject(
  orderId: number,
  payload: ProjectRejectPayload,
): Promise<ProjectRejectResult> {
  return api.post<ProjectRejectResult>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ablehnen`,
    {
      begruendung: payload.begruendung,
      stunden_aktion: payload.stunden_aktion ?? null,
      ziel_auftrag_id: payload.ziel_auftrag_id ?? null,
    },
  );
}

export function projectRejectSuccessMessage(result: {
  stunden_aktion?: string | null;
}): string {
  if (result.stunden_aktion === "umbuchen") {
    return "Projekt abgelehnt. Die Stunden wurden umgebucht.";
  }
  if (result.stunden_aktion === "verfallen") {
    return "Projekt abgelehnt. Die Stunden bleiben auf dem Projekt und verfallen mit.";
  }
  return "Projekt abgelehnt.";
}

/** Parameter für `GET /auftraege`. */
export interface ListOrdersParams {
  q?: string;
  jahr?: string | number;
  kunde?: string;
  /** App-Kunde (geschaeftspartner.id): nur Projekte dieses Kunden. */
  geschaeftspartner_id?: number;
  ordner?: string;
  sordner?: string;
  /** Einzelner Status oder kommagetrennte Liste (z. B. `offen,beauftragt`). */
  status?: "alle" | "abgeschlossen" | ProjectWorkflowStatusKey | string;
  limit?: number;
  offset?: number;
  sort?: string;
  direction?: "asc" | "desc";
  include_timesheets?: boolean;
  include_timesheet_numbers?: boolean;
  include_tags?: boolean;
  include_hero_custom_fields?: boolean;
  include_hero_steps?: boolean;
  /** Hybride Suche: ausführliche Treffergründe (interne Codes) anfordern. */
  debug?: boolean;
  /** Hybride Suche: semantische Quelle erzwingen (true) / abschalten (false). */
  semantic?: boolean;
}

export interface OrdnerOut {
  id: number;
  nummer: string;
  beschreibung: string;
  jahr: number;
  branch_id?: number | null;
  measure_id?: number | null;
  project_type_id?: number | null;
  project_step_id?: number | null;
  hero_custom_field_defaults?: HeroCustomFieldValue[] | null;
  /** Bei Projekterstellung fragen, ob beauftragt wurde. */
  ask_commission_after_create?: boolean;
  /** Bei Projekterstellung Schadensmeldung / FM-Auftrag hochladen erlauben. */
  allow_schadensmeldung_on_create?: boolean;
  kunde_id: number;
  kunden_code: number;
  kunden_name: string;
}

export interface SOrdnerOut {
  id: number;
  nummer: string;
  beschreibung?: string | null;
  kunde_id?: number | null;
  kunden_code?: number | null;
  kunden_name?: string | null;
  hero_customer_id?: number | null;
  /** App-Kunde (geschaeftspartner.id), auch ohne Hero-Verknüpfung. */
  geschaeftspartner_id?: number | null;
  hero_address_id?: number | null;
  /** Ort-Zusatz (Freitext am S-Ordner). */
  ort_name_text?: string | null;
  /** Anzeigename aus orte (Hero-Objektadresse). */
  ort_name?: string | null;
  last_auftrag_nummer?: number | null;
  jahr?: number | null;
  sharepoint_folder_id?: string | null;
  /** Hero-Dokument-ID des als „aktuell“ markierten Angebots/Dokuments. */
  current_hero_document_id?: number | null;
}

export interface HeroCustomFieldProperty {
  uuid: string;
  type: string;
  label: string;
  position: number;
  hint?: string | null;
  options?: unknown;
  enabled: boolean;
  showOnTimesheet: boolean;
  showOnFolderDefaults: boolean;
}

export interface HeroCustomFieldSchema {
  schemaId: string;
  relates: string;
  properties: HeroCustomFieldProperty[];
}

/* ───────────────── Projekt-Tabelle: Spalten-Settings ──────────────── */

export interface ProjectColumnView {
  /** Spaltenliste (Reihenfolge). Pflichtspalten ggf. ergänzen. */
  columns: string[];
  /** Spaltenbreiten in Pixel je Schlüssel. */
  widths: Record<string, number>;
}

export interface ProjectColumnSettings {
  desktop: ProjectColumnView | null;
  mobile: ProjectColumnView | null;
}

export interface ProjectColumnSettingsResponse {
  table_key: string;
  settings: ProjectColumnSettings | null;
}

const PROJECT_TABLE_KEY = "projects";

/* ─────────────────────────── Helper ───────────────────────────── */

function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (typeof value === "boolean") {
      search.set(key, value ? "1" : "0");
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/* ─────────────────────────── API-Calls ────────────────────────── */

export interface ListOrdersResult {
  orders: Order[];
  /** Gesamtanzahl für die aktuellen Filter (Header `X-Total-Count`). */
  totalCount: number | null;
}

function parseTotalCountHeader(headers: Headers): number | null {
  const raw = headers.get("X-Total-Count");
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function buildListOrdersQuery(params: ListOrdersParams = {}): Record<string, unknown> {
  const cleaned: Record<string, unknown> = { ...params };
  if (cleaned.status === "alle" || cleaned.status === "") delete cleaned.status;
  // Standard-Includes – die Tabelle nutzt sie alle.
  if (cleaned.include_hero_custom_fields === undefined) {
    cleaned.include_hero_custom_fields = true;
  }
  if (cleaned.include_tags === undefined) cleaned.include_tags = true;
  return cleaned;
}

/** Liste der Aufträge inkl. Gesamtanzahl für die aktiven Filter. */
export async function listOrdersWithTotal(
  params: ListOrdersParams = {},
  opts?: { signal?: AbortSignal },
): Promise<ListOrdersResult> {
  const cleaned = buildListOrdersQuery(params);
  const { data, headers } = await api.getWithMeta<Order[]>(
    `/auftraege${buildQuery(cleaned)}`,
    opts?.signal ? { signal: opts.signal } : undefined,
  );
  return {
    orders: Array.isArray(data) ? data : [],
    totalCount: parseTotalCountHeader(headers),
  };
}

/** Liste der Aufträge. Status `alle` wird vor dem Request entfernt. */
export function listOrders(params: ListOrdersParams = {}): Promise<Order[]> {
  return listOrdersWithTotal(params).then((result) => result.orders);
}

/** Lädt alle Aufträge für die Filter paginiert (API-Limit 200 pro Seite). */
export async function listAllOrders(params: ListOrdersParams = {}): Promise<Order[]> {
  const pageSize = 200;
  let offset = 0;
  const all: Order[] = [];
  let totalCount: number | null = null;

  while (true) {
    const { orders, totalCount: total } = await listOrdersWithTotal({
      ...params,
      limit: pageSize,
      offset,
    });
    all.push(...orders);
    totalCount = total ?? all.length;
    if (orders.length < pageSize || all.length >= totalCount) break;
    offset += pageSize;
  }

  return all;
}

export interface HeroProjectStepInfo {
  hero_step_id?: number | null;
  hero_step_name?: string | null;
  hero_kunde?: string | null;
}

/**
 * Lädt Hero-Status-Step + Kundenname-Fallback je Hero-Projekt-ID. Wird vom
 * Projekte-Seitencontroller asynchron nach dem (schnellen) DB-Listen-Load
 * aufgerufen, damit der erste Render nicht auf die Hero-API wartet.
 */
export async function fetchHeroProjectSteps(
  heroProjectIds: number[],
): Promise<Map<number, HeroProjectStepInfo>> {
  const out = new Map<number, HeroProjectStepInfo>();
  const ids = Array.from(
    new Set(heroProjectIds.filter((id) => Number.isFinite(id) && id > 0)),
  );
  if (!ids.length) return out;

  const CHUNK = 200;
  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += CHUNK) {
    chunks.push(ids.slice(i, i + CHUNK));
  }

  await Promise.all(
    chunks.map(async (slice) => {
      try {
        const data = await api.get<Record<string, HeroProjectStepInfo>>(
          `/auftraege/hero-steps/batch?hero_project_ids=${encodeURIComponent(slice.join(","))}`,
        );
        for (const [key, value] of Object.entries(data || {})) {
          const id = Number(key);
          if (!Number.isFinite(id)) continue;
          out.set(id, {
            hero_step_id: value?.hero_step_id ?? null,
            hero_step_name: value?.hero_step_name ?? null,
            hero_kunde: value?.hero_kunde ?? null,
          });
        }
      } catch (err) {
        console.warn("[Projekte] Hero-Steps laden fehlgeschlagen:", err);
      }
    }),
  );

  return out;
}

/** Liste eindeutiger Jahre, die wir als Filter anbieten. */
export async function listYears(): Promise<number[]> {
  const list = await api.get<number[]>("/ordner/jahre");
  return Array.isArray(list) ? list.map((y) => Number(y)).filter((y) => !Number.isNaN(y)) : [];
}

/** Alle Ordner (optional gefiltert nach Jahr). */
// Die FilterBars von Projekte und Stundennachweise lösen `listOrdner(jahr)` beim
// Laden doppelt aus: einmal direkt für die Ordner-Chips und einmal indirekt über
// `listKundenForYear`. Ein kurzlebiger Promise-Cache pro Jahr teilt diese
// parallelen Aufrufe und halbiert die `/ordner`-Requests beim Seitenstart.
const ORDNER_TTL_MS = 15_000;
const _ordnerCache = new Map<string, { at: number; promise: Promise<OrdnerOut[]> }>();

export function listOrdner(jahr?: number): Promise<OrdnerOut[]> {
  const key = jahr == null ? "all" : String(jahr);
  const cached = _ordnerCache.get(key);
  if (cached && Date.now() - cached.at < ORDNER_TTL_MS) {
    return cached.promise;
  }
  const promise = api
    .get<OrdnerOut[]>(`/ordner${buildQuery({ jahr })}`)
    .catch((err) => {
      _ordnerCache.delete(key);
      throw err;
    });
  _ordnerCache.set(key, { at: Date.now(), promise });
  return promise;
}

/** Liefert die Ordner eines Kunden (optional auf ein Jahr eingegrenzt). */
export function listOrdnerForYearCustomer(
  kunde: string,
  jahr?: number,
): Promise<OrdnerOut[]> {
  return api.get<OrdnerOut[]>(`/ordner/kunde${buildQuery({ kunde, jahr })}`);
}

/** Eindeutige Kundennamen für ein Jahr – aus den Ordnern abgeleitet. */
export async function listKundenForYear(jahr: number): Promise<string[]> {
  const ordner = await listOrdner(jahr);
  const set = new Set<string>();
  for (const o of ordner) {
    if (o.kunden_name) set.add(o.kunden_name);
  }
  return [...set].sort((a, b) => a.localeCompare(b, "de"));
}

/** Eindeutige Kundennamen aus allen Ordnern (z. B. Filter „Alle Jahre“). */
export async function listKundenAllFromOrdner(): Promise<string[]> {
  const ordner = await listOrdner();
  const set = new Set<string>();
  for (const o of ordner) {
    if (o.kunden_name) set.add(o.kunden_name);
  }
  return [...set].sort((a, b) => a.localeCompare(b, "de"));
}

const SORDNER_LIST_TTL_MS = 2 * 60_000;

let sordnerSnapshot: SOrdnerOut[] | null = null;
let sordnerSnapshotAt = 0;
let sordnerInflight: Promise<SOrdnerOut[]> | null = null;

/** Bereits geladene S-Ordner, auch wenn der Cache gerade neu geholt wird. */
export function peekSOrdnerList(): SOrdnerOut[] | null {
  return sordnerSnapshot;
}

export function findSOrdnerByNummer(
  list: readonly SOrdnerOut[],
  nummer: string,
): SOrdnerOut | undefined {
  const trimmed = nummer.trim();
  const needle = trimmed.toLowerCase();
  if (!needle) return undefined;
  return list.find((s) => s.nummer.trim().toLowerCase() === needle || s.nummer === trimmed);
}

/**
 * Liste aller S-Ordner.
 * Parallele Aufrufe teilen sich eine Anfrage; innerhalb der TTL kommt die
 * letzte Liste sofort zurück, damit Ordnerwechsel nicht neu laden müssen.
 */
export function listSOrdner(): Promise<SOrdnerOut[]> {
  if (sordnerSnapshot && Date.now() - sordnerSnapshotAt < SORDNER_LIST_TTL_MS) {
    return Promise.resolve(sordnerSnapshot);
  }
  if (sordnerInflight) return sordnerInflight;
  const promise = api
    .get<SOrdnerOut[]>("/sordner")
    .then((list) => {
      const rows = Array.isArray(list) ? list : [];
      sordnerSnapshot = rows;
      sordnerSnapshotAt = Date.now();
      return rows;
    })
    .finally(() => {
      if (sordnerInflight === promise) sordnerInflight = null;
    });
  sordnerInflight = promise;
  return promise;
}

/** Einzelnen S-Ordner inkl. Metadaten laden. */
export function getSOrdner(id: number): Promise<SOrdnerOut> {
  return api.get<SOrdnerOut>(`/sordner/${encodeURIComponent(String(id))}`);
}

/** SharePoint-Web-URL zum S-Ordner (falls verknüpft). */
export async function getSOrdnerSharepointWebUrl(sordnerId: number): Promise<string | null> {
  try {
    const res = await api.get<{ webUrl?: string | null }>(
      `/sordner/${encodeURIComponent(String(sordnerId))}/sharepoint-url`,
    );
    return res?.webUrl ?? null;
  } catch {
    return null;
  }
}

/* ─────────────────────── Tabellen-Settings ─────────────────── */

export async function getColumnSettings(): Promise<ProjectColumnSettings> {
  const res = await api.get<ProjectColumnSettingsResponse>(
    `/me/table-settings/${PROJECT_TABLE_KEY}`,
  );
  const settings = res?.settings ?? null;
  return {
    desktop: settings?.desktop ?? null,
    mobile: settings?.mobile ?? null,
  };
}

/** SharePoint-Web-URL zum Auftragsordner (Fallback, wenn lokales Öffnen fehlschlägt). */
export async function getAuftragSharepointWebUrl(auftragId: number): Promise<string | null> {
  try {
    const res = await api.get<{ webUrl?: string | null }>(
      `/auftraege/${auftragId}/sharepoint-url`,
    );
    return res?.webUrl ?? null;
  } catch {
    return null;
  }
}

/** Lokaler Projektordner in Electron; bei Fehler SharePoint-Web-URL (wie klassische App). */
export async function openOrderFolder(
  order: Pick<Order, "id" | "ordnerpfad" | "sharepoint_path">,
): Promise<void> {
  const path = (order.ordnerpfad || order.sharepoint_path || "").trim();
  if (!path) {
    const webUrl = await getAuftragSharepointWebUrl(order.id);
    if (webUrl) window.open(webUrl, "_blank", "noopener");
    return;
  }
  const openFolder = window.envAPI?.openFolder;
  if (typeof openFolder !== "function") return;
  const err = await openFolder(path);
  if (err) {
    const webUrl = await getAuftragSharepointWebUrl(order.id);
    if (webUrl) {
      window.open(webUrl, "_blank", "noopener");
      return;
    }
    const { toast } = await import("@/lib/toast");
    toast.error(String(err));
  }
}

export async function saveColumnSettings(
  settings: ProjectColumnSettings,
): Promise<ProjectColumnSettings> {
  const res = await api.post<{ ok: true; settings: ProjectColumnSettings }>(
    `/me/table-settings/${PROJECT_TABLE_KEY}`,
    { settings },
  );
  return {
    desktop: res?.settings?.desktop ?? null,
    mobile: res?.settings?.mobile ?? null,
  };
}

/* ─────────────────────── Hero-Custom-Fields ─────────────────── */

// `/hero/custom-fields/schemas` löst im Backend einen Hero-GraphQL-Call (~20s
// Timeout) + DB-Upsert aus. Auf den Projekt-/Stundennachweis-Seiten wird der
// Endpunkt beim Laden mehrfach angefragt (Spalten-Metadaten + useHoursUsage).
// Ein kurzlebiger Promise-Cache teilt parallele Aufrufe und vermeidet Doppel-
// Round-Trips, ohne dass Schema-Änderungen lange unsichtbar bleiben.
const HERO_SCHEMAS_TTL_MS = 60_000;
let _heroSchemasCache: { at: number; promise: Promise<HeroCustomFieldSchema[]> } | null = null;

function fetchHeroCustomFieldSchemas(): Promise<HeroCustomFieldSchema[]> {
  if (_heroSchemasCache && Date.now() - _heroSchemasCache.at < HERO_SCHEMAS_TTL_MS) {
    return _heroSchemasCache.promise;
  }
  const promise = api
    .get<HeroCustomFieldSchema[]>("/hero/custom-fields/schemas")
    .then((all) => (Array.isArray(all) ? all : []))
    .catch((err) => {
      _heroSchemasCache = null;
      throw err;
    });
  _heroSchemasCache = { at: Date.now(), promise };
  return promise;
}

/** Schemas, die Projekten zugeordnet sind (`relates === 'project'`). */
export async function getProjectCustomFieldSchemas(): Promise<HeroCustomFieldSchema[]> {
  const all = await fetchHeroCustomFieldSchemas();
  return all.filter((schema) => {
    const rel = String(schema?.relates || "").toLowerCase();
    // Hero verwendet im Projekt-Kontext den Begriff "project".
    return rel === "project" || rel === "projects";
  });
}

/**
 * Liefert die als „Tabellenfelder" markierten Properties für die Projekt-
 * tabelle: alle aktivierten Properties aller passenden Schemas.
 */
export async function getProjectTableHeroFields(): Promise<
  Array<{ schemaId: string; uuid: string; label: string }>
> {
  const schemas = await getProjectCustomFieldSchemas();
  const out: Array<{ schemaId: string; uuid: string; label: string }> = [];
  for (const schema of schemas) {
    for (const prop of schema.properties || []) {
      if (!prop.enabled) continue;
      if (isClaimedHeroCustomFieldLabel(prop.label)) continue;
      out.push({
        schemaId: schema.schemaId,
        uuid: prop.uuid,
        label: prop.label || "Eigenes Feld",
      });
    }
  }
  return out;
}

/* ─────────────────────── Hero-Stammdaten (Lookups) ─────────────────── */

export interface HeroCustomerLookup {
  /** Hero-Contact-ID; fehlt bei rein lokal angelegten App-Kunden. */
  id?: number | null;
  code?: number | string | null;
  name: string;
  /** App-Kunde (geschaeftspartner.id), wenn aus Kontakten aufgelöst. */
  geschaeftspartner_id?: number | null;
}

export interface HeroContactLookup {
  id: number;
  full_name: string;
}

export interface HeroAddressLookup {
  address_id: number;
  title: string;
  street?: string | null;
  city?: string | null;
  zipcode?: string | null;
  maps_link?: string | null;
  /** Lokale orte.id, wenn aus App-Orten aufgelöst. */
  ort_id?: number | null;
}

export interface HeroBranchLookup {
  id: number;
  name: string;
}

export interface HeroMeasureLookup {
  id: number;
  name?: string | null;
  short?: string | null;
}

export interface HeroProjectTypeLookup {
  id: number;
  name: string;
}

export interface HeroProjectStepLookup {
  id: number;
  name: string;
}

export interface HeroProjectStepGroup {
  project_status_steps?: HeroProjectStepLookup[];
}

export interface HeroEmployeeLookup {
  user_id: number;
  name?: string | null;
  email?: string | null;
  account_type?: string | null;
}

export interface DoorSearchResult {
  door_number?: string | null;
  label?: string | null;
  value?: string | null;
}

/* ────── Kurzlebiger Cache für kundenbezogene Hero-Lookups ──────
 *
 * Diese Hero-Endpunkte sind langsam (GraphQL gegen Hero) und werden von den
 * Autocomplete-Feldern im New-Project-Dialog pro Tastenanschlag aufgerufen.
 * Die zugrunde liegenden Listen (Kunden zu einem Code, Ansprechpartner und
 * Adressen eines Kunden) ändern sich während einer Dialog-Sitzung praktisch
 * nicht. Wir laden sie daher genau einmal pro Schlüssel, halten die Promise im
 * Cache (inkl. In-Flight-Deduplizierung) und filtern in den Autocomplete-
 * Quellen rein clientseitig. Das eliminiert die mehrsekündigen Wartezeiten
 * beim Tippen.
 */
const HERO_LOOKUP_TTL_MS = 5 * 60_000;

type HeroLookupCacheEntry<T> = { at: number; promise: Promise<T[]> };

function getCachedHeroLookup<T>(
  cache: Map<string, HeroLookupCacheEntry<T>>,
  key: string,
  fetcher: () => Promise<T[]>,
): Promise<T[]> {
  const existing = cache.get(key);
  if (existing && Date.now() - existing.at < HERO_LOOKUP_TTL_MS) {
    return existing.promise;
  }
  const promise = fetcher()
    .then((list) => (Array.isArray(list) ? list : []))
    .catch((err) => {
      // Fehlgeschlagene Anfragen nicht cachen, damit ein Retry möglich bleibt.
      const current = cache.get(key);
      if (current && current.promise === promise) cache.delete(key);
      throw err;
    });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

const _customersByCodeCache = new Map<string, HeroLookupCacheEntry<HeroCustomerLookup>>();
const _companyContactsCache = new Map<string, HeroLookupCacheEntry<HeroContactLookup>>();
const _customerAddressesCache = new Map<string, HeroLookupCacheEntry<HeroAddressLookup>>();

export function getHeroCustomersByCode(code: string | number): Promise<HeroCustomerLookup[]> {
  const key = String(code);
  return getCachedHeroLookup(_customersByCodeCache, key, () =>
    api.get<HeroCustomerLookup[]>(
      `/hero/customers-by-code/${encodeURIComponent(key)}`,
    ),
  );
}

// `_q` wird bewusst ignoriert: Wir laden die vollständige Liste einmal pro Kunde
// (cachebar) und filtern in der Autocomplete-Quelle clientseitig.
export function getHeroContacts(
  kundeId: number,
  _q = "",
): Promise<HeroContactLookup[]> {
  const key = String(kundeId);
  return getCachedHeroLookup(_companyContactsCache, key, () =>
    api.get<HeroContactLookup[]>(
      `/hero/company-contacts/${encodeURIComponent(key)}`,
    ),
  );
}

export function getHeroAddresses(
  kundeId: number,
  _q = "",
  options?: { forceRefresh?: boolean },
): Promise<HeroAddressLookup[]> {
  const key = String(kundeId);
  if (options?.forceRefresh) {
    _customerAddressesCache.delete(key);
  }
  return getCachedHeroLookup(_customerAddressesCache, key, () =>
    api.get<HeroAddressLookup[]>(
      `/hero/customer-addresses/${encodeURIComponent(key)}`,
    ),
  );
}

/* ────── Module-Cache für statische Hero-Lookups ──────
 *
 * Die folgenden Hero-Listen ändern sich pro Session faktisch nie und werden
 * an mehreren Stellen (z. B. NewProjectDialog) zur Vorbelegung benötigt.
 * Wir cachen daher die Promise selbst – nicht nur das Ergebnis – damit
 * parallele Aufrufer ein und dieselbe Promise teilen und nicht jeder eigens
 * lädt.  Bei Fehlern wird der Cache invalidiert, damit ein erneuter Versuch
 * möglich bleibt.
 */
let _branchesCache: Promise<HeroBranchLookup[]> | null = null;
let _measuresCache: Promise<HeroMeasureLookup[]> | null = null;
let _projectTypesCache: Promise<HeroProjectTypeLookup[]> | null = null;
const _projectStepsCache = new Map<number, Promise<HeroProjectStepLookup[]>>();

export function getHeroBranches(): Promise<HeroBranchLookup[]> {
  if (!_branchesCache) {
    _branchesCache = api
      .get<HeroBranchLookup[]>("/hero/company-branches")
      .catch((err) => {
        _branchesCache = null;
        throw err;
      });
  }
  return _branchesCache;
}

export function getHeroMeasures(): Promise<HeroMeasureLookup[]> {
  if (!_measuresCache) {
    _measuresCache = api
      .get<HeroMeasureLookup[]>("/hero/measures")
      .catch((err) => {
        _measuresCache = null;
        throw err;
      });
  }
  return _measuresCache;
}

export function getHeroProjectTypes(): Promise<HeroProjectTypeLookup[]> {
  if (!_projectTypesCache) {
    _projectTypesCache = api
      .get<HeroProjectTypeLookup[]>("/hero/project-types")
      .catch((err) => {
        _projectTypesCache = null;
        throw err;
      });
  }
  return _projectTypesCache;
}

export function getHeroProjectSteps(
  projectTypeId: number,
): Promise<HeroProjectStepLookup[]> {
  const key = Number(projectTypeId);
  const existing = _projectStepsCache.get(key);
  if (existing) return existing;
  const promise = (async () => {
    const list = await api.get<HeroProjectStepGroup[]>(
      `/hero/project-status-steps/${encodeURIComponent(String(key))}`,
    );
    if (!Array.isArray(list) || !list.length) return [];
    return list[0]?.project_status_steps ?? [];
  })().catch((err) => {
    _projectStepsCache.delete(key);
    throw err;
  });
  _projectStepsCache.set(key, promise);
  return promise;
}

/**
 * Setzt den Hero-Lookup-Cache zurück.  Wird typischerweise nicht benötigt;
 * vorhanden für Tests oder gezielte Reloads.
 */
export function clearHeroLookupCache(): void {
  _branchesCache = null;
  _measuresCache = null;
  _projectTypesCache = null;
  _projectStepsCache.clear();
}

export async function getHeroEmployees(): Promise<HeroEmployeeLookup[]> {
  const list = await api.get<HeroEmployeeLookup[]>("/hero/employees");
  if (!Array.isArray(list)) return [];
  // Mobile-Accounts wie in der klassischen App.
  return list.filter((p) => String(p.account_type || "").toLowerCase() === "mobile");
}

/** Monteure (mobile) und Voll-Lizenzen (full) für die Montageplanung. */
export async function getHeroPlanningEmployees(): Promise<HeroEmployeeLookup[]> {
  const list = await api.get<HeroEmployeeLookup[]>("/hero/employees");
  if (!Array.isArray(list)) return [];
  return list.filter((p) => {
    const t = String(p.account_type || "").toLowerCase();
    return t === "mobile" || t === "full";
  });
}

export function searchDoors(q = ""): Promise<DoorSearchResult[]> {
  return api.get<DoorSearchResult[]>(`/tuerwartung/doors/search${buildQuery({ q })}`);
}

/* ─────────────────────── Projekt-Anlage ─────────────────── */

export interface CreateOrderHeroCustomFieldValue {
  schemaId: string;
  propertyUuid: string;
  value: string;
}

export interface CreateOrderPayload {
  ordner_id: number | null;
  sordner_id: number | null;
  nummer: number;
  ansprechpartner_id: number;
  ort_id: number | null;
  hero_address_id: number | null;
  ort_name_text: string;
  ort_zusatz: string | null;
  beschreibung: string;
  /** Nur noch von älteren Clients; die App legt Projekte ohne Hero-Metadaten an. */
  project_type_id?: number | null;
  project_step_id?: number | null;
  jahr: number;
  hero_customer_id: number | null;
  /** App-Kunde, auch wenn keine Hero-ID existiert. */
  geschaeftspartner_id?: number | null;
  measure_id?: number | null;
  branch_id?: number | null;
  assigned_user_ids?: number[];
  hero_custom_fields?: CreateOrderHeroCustomFieldValue[];
  bestellnummer?: string | null;
  kundenreferenz?: string | null;
  leitweg_id?: string | null;
  /** @deprecated Legacy Türwartung – bitte asset_ids verwenden. */
  door_number?: string | null;
  /** Verknüpfte Wartungsobjekte (neue Wartung). */
  asset_ids?: number[];
  print_cover_on_create: boolean;
  print_location_id: number | null;
  print_copies: number;
}

export interface CreatedOrder {
  id: number;
  auftragsnummer?: string;
  /**
   * Client-seitig: Im Anlege-Dialog mit „Ja, bereits beauftragt“ bestätigt –
   * öffnet danach den mündlichen Beauftragungs-Flow.
   * Wird nicht vom API-Create geliefert.
   */
  ask_commission_after_create?: boolean;
}

export function createOrder(payload: CreateOrderPayload): Promise<CreatedOrder> {
  return api.post<CreatedOrder>("/auftraege", payload);
}

/* ─────────────────────── Hero-Projekt anlegen ─────────────────── */

export interface HeroProjectCreateMeta {
  project_type_id?: number | null;
  branch_id?: number | null;
  measure_id?: number | null;
  project_step_id?: number | null;
}

export interface HeroProjectCreatePayload {
  project_step_id: number;
  measure_id: number;
  branch_id: number;
  project_type_id?: number | null;
}

export interface HeroProjectCreateResult {
  hero_project_id: number;
}

export function getHeroCreateMeta(
  auftragId: number,
): Promise<HeroProjectCreateMeta> {
  return api.get<HeroProjectCreateMeta>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/hero-create-meta`,
  );
}

export function createHeroProject(
  auftragId: number,
  payload: HeroProjectCreatePayload,
): Promise<HeroProjectCreateResult> {
  return api.post<HeroProjectCreateResult>(
    `/auftraege/${encodeURIComponent(String(auftragId))}/hero-create`,
    payload,
  );
}

export interface NextNummerResponse {
  naechste_nummer: number;
}

export function getNextNummer(
  ordner: string,
  jahr: number,
  ordnerId?: number,
): Promise<NextNummerResponse> {
  return api.get<NextNummerResponse>(
    `/auftraege/naechste_nummer${buildQuery({ ordner, jahr, ordner_id: ordnerId })}`,
  );
}

export interface OrdnerUmzugPreview {
  ordner_id: number;
  ordner_nummer: string;
  ordner_beschreibung: string;
  ordner_jahr: number;
  bereits_dort: boolean;
  nummer_belegt: boolean;
  nummer: number;
  auftragsnummer: string;
  auftrag_id?: number;
  alte_auftragsnummer?: string;
}

export function previewOrdnerUmzug(
  orderId: number,
  ordnerId: number,
): Promise<OrdnerUmzugPreview> {
  return api.get<OrdnerUmzugPreview>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ordner-umzug${buildQuery({ ordner_id: ordnerId })}`,
  );
}

export function moveProjectOrdner(
  orderId: number,
  ordnerId: number,
): Promise<OrdnerUmzugPreview> {
  return api.post<OrdnerUmzugPreview>(
    `/auftraege/${encodeURIComponent(String(orderId))}/ordner-umzug`,
    { ordner_id: ordnerId },
  );
}

export function getNextNummerS(sordner: string): Promise<NextNummerResponse> {
  return api.get<NextNummerResponse>(
    `/auftraege/naechste_nummer_s${buildQuery({ sordner })}`,
  );
}

export function setOrderTags(orderId: number, tags: string[]): Promise<unknown> {
  return api.put(`/auftraege/${encodeURIComponent(String(orderId))}/tags`, { tags });
}

export interface BeschreibungAssistResponse {
  suggested_beschreibung?: string | null;
  reason?: string | null;
  schlagworte?: string[] | null;
}

export function runBeschreibungAssist(text: string): Promise<BeschreibungAssistResponse> {
  return api.post<BeschreibungAssistResponse>("/auftraege/beschreibung-assist", { text });
}

/* ─────────────────────── Druck / Cover ─────────────────── */

export interface PrintLocation {
  id?: number;
  key?: string | number;
  value?: string | number;
  name?: string;
  label?: string;
}

export function getPrintLocations(): Promise<PrintLocation[]> {
  return api.get<PrintLocation[]>("/print/locations");
}

export interface DefaultPrintLocationResponse {
  default_location_id?: number | null;
}

export function getDefaultPrintLocation(): Promise<DefaultPrintLocationResponse> {
  return import("@/lib/printPreferences").then((m) => m.getDefaultPrintLocation());
}

export async function setDefaultPrintLocation(
  locationId: number | null,
): Promise<DefaultPrintLocationResponse> {
  const { setDefaultPrintLocation: save } = await import("@/lib/printPreferences");
  return save(locationId);
}

export interface CoverPrintUrlResponse {
  url: string;
}

export function getCoverPrintUrl(orderId: number): Promise<CoverPrintUrlResponse> {
  return api.get<CoverPrintUrlResponse>(
    `/auftraege/${encodeURIComponent(String(orderId))}/cover/pdf/print-url`,
    { cache: "no-store" },
  );
}

export {
  printByLocationAndWait,
  queuePrintByLocation,
  runPrintByLocationWithFeedback,
} from "@/lib/printJob";

/** @deprecated Verwende `printByLocationAndWait` oder `runPrintByLocationWithFeedback`. */
export async function printByLocation(
  locationId: number,
  copies: number,
  fileUrl: string,
): Promise<unknown> {
  const { queuePrintByLocation } = await import("@/lib/printJob");
  return queuePrintByLocation(locationId, copies, fileUrl);
}

/* ─────────────────────── Tag-Vorschläge ─────────────────── */

export interface TagSuggestionResponse {
  suggestions?: Array<{ name: string }>;
}

/**
 * Vorschläge aus den Tags eines bestehenden Auftrags ableiten – das macht
 * der Backend-Endpunkt: er liefert neben den eigenen Tags auch eine Liste
 * `suggestions` mit häufig benutzten Schlagwörtern.
 */
export async function getTagSuggestions(seedOrderId: number): Promise<string[]> {
  const data = await api
    .get<TagSuggestionResponse>(
      `/auftraege/${encodeURIComponent(String(seedOrderId))}/tags`,
      { cache: "no-store" },
    )
    .catch(() => ({}) as TagSuggestionResponse);
  const list = data?.suggestions ?? [];
  return list.map((t) => String(t?.name || "").trim()).filter(Boolean);
}
